// components/VarianteSelector/VarianteSelector.js
import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Button, Form, Badge, Alert, Spinner } from 'react-bootstrap'
import api from 'api/api'
import './VarianteSelector.css'

/**
 * Modal para seleccionar variantes/atributos de un producto
 * 
 * @param {boolean} show - Si mostrar el modal
 * @param {object} producto - Producto seleccionado
 * @param {function} onConfirm - Callback al confirmar (producto, cantidad, atributosSeleccionados)
 * @param {function} onCancel - Callback al cancelar
 * @param {number} cantidadInicial - Cantidad inicial (default 1)
 */
function VarianteSelector({
	show = false,
	producto = null,
	onConfirm,
	onCancel,
	cantidadInicial = 1
}) {
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [atributosDisponibles, setAtributosDisponibles] = useState([])
	const [selecciones, setSelecciones] = useState({}) // { tipoId: valorId }
	const [cantidad, setCantidad] = useState(cantidadInicial)

	// Cargar atributos del producto cuando se abre el modal
	useEffect(() => {
		if (show && producto) {
			cargarAtributos()
			setCantidad(cantidadInicial)
			setSelecciones({})
			setError(null)
		}
	}, [show, producto, cantidadInicial])

	const cargarAtributos = async () => {
		if (!producto?.id) return

		setLoading(true)
		setError(null)

		try {
			// Obtener atributos configurados del producto
			const response = await api.get(`a/items/${producto.id}/atributos`)

			// La respuesta viene en response.datos.atributos
			const atributosData = response.datos?.atributos || []

			if (atributosData && atributosData.length > 0) {
				// Filtrar tipos que tienen valores SELECCIONADOS para este producto
				const tiposConValoresSeleccionados = atributosData
					.map(tipo => {
						// Filtrar solo los valores que están seleccionados para este producto
						const valoresSeleccionados = (tipo.valores || []).filter(v => v.seleccionado === true)

						return {
							...tipo,
							valores: valoresSeleccionados
						}
					})
					.filter(tipo => tipo.valores && tipo.valores.length > 0)

				setAtributosDisponibles(tiposConValoresSeleccionados)

				// Pre-seleccionar el primer valor de cada tipo si solo hay uno
				const seleccionesIniciales = {}
				tiposConValoresSeleccionados.forEach(tipo => {
					if (tipo.valores.length === 1) {
						// Si solo hay un valor, seleccionarlo automáticamente
						seleccionesIniciales[tipo.id] = tipo.valores[0].id
					}
				})
				setSelecciones(seleccionesIniciales)
			} else {
				setAtributosDisponibles([])
			}
		} catch (err) {
			console.error('Error al cargar atributos:', err)
			setError('No se pudieron cargar los atributos del producto')
		} finally {
			setLoading(false)
		}
	}

	// Manejar cambio de selección
	const handleSeleccionChange = (tipoId, valorId) => {
		setSelecciones(prev => ({
			...prev,
			[tipoId]: valorId ? parseInt(valorId) : null
		}))
	}

	// Verificar si la selección está completa
	const seleccionCompleta = useCallback(() => {
		if (atributosDisponibles.length === 0) return true

		return atributosDisponibles.every(tipo =>
			selecciones[tipo.id] !== undefined && selecciones[tipo.id] !== null
		)
	}, [atributosDisponibles, selecciones])

	// Obtener resumen de selección
	const getResumenSeleccion = useCallback(() => {
		const resumen = []
		atributosDisponibles.forEach(tipo => {
			const valorId = selecciones[tipo.id]
			if (valorId) {
				const valor = tipo.valores.find(v => v.id === valorId)
				if (valor) {
					resumen.push({
						tipoId: tipo.id,
						tipoNombre: tipo.nombre,
						valorId: valor.id,
						// El servidor usa "nombre" para el valor, no "valor"
						valorNombre: valor.nombre || valor.valor
					})
				}
			}
		})
		return resumen
	}, [atributosDisponibles, selecciones])

	// Confirmar selección
	const handleConfirm = () => {
		if (!seleccionCompleta()) {
			setError('Por favor seleccione todas las opciones')
			return
		}

		const atributosSeleccionados = getResumenSeleccion()
		onConfirm(producto, cantidad, atributosSeleccionados)
	}

	// Cancelar
	const handleCancel = () => {
		setSelecciones({})
		setCantidad(cantidadInicial)
		onCancel()
	}

	if (!producto) return null

	return (
		<Modal
			show={show}
			onHide={handleCancel}
			centered
			className="variante-selector-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className="fa fa-tags me-2"></i>
					Seleccionar Variante
				</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				{/* Info del producto */}
				<div className="producto-info mb-4">
					<h5 className="mb-1">{producto.nombre}</h5>
					{producto.codigo && (
						<small className="text-muted">Código: {producto.codigo}</small>
					)}
				</div>

				{loading ? (
					<div className="text-center py-4">
						<Spinner animation="border" variant="primary" />
						<p className="mt-2 text-muted">Cargando opciones...</p>
					</div>
				) : error ? (
					<Alert variant="danger">
						<i className="fa fa-exclamation-circle me-2"></i>
						{error}
					</Alert>
				) : atributosDisponibles.length === 0 ? (
					<Alert variant="info">
						<i className="fa fa-info-circle me-2"></i>
						Este producto no tiene variantes configuradas.
						Se agregará directamente al pedido.
					</Alert>
				) : (
					<>
						{/* Selectores de atributos */}
						<div className="atributos-selectores">
							{atributosDisponibles.map(tipo => (
								<Form.Group key={tipo.id} className="mb-3">
									<Form.Label className="fw-bold">
										{tipo.nombre}
										<span className="text-danger ms-1">*</span>
										{tipo.heredado && (
											<Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
												Heredado
											</Badge>
										)}
										{tipo.directo && (
											<Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
												Directo
											</Badge>
										)}
									</Form.Label>

									{/* Mostrar como botones si hay pocos valores */}
									{tipo.valores.length <= 8 ? (
										<div className="valores-botones">
											{tipo.valores.map(valor => (
												<Button
													key={valor.id}
													variant={selecciones[tipo.id] === valor.id ? 'primary' : 'outline-secondary'}
													size="sm"
													className="me-2 mb-2"
													onClick={() => handleSeleccionChange(tipo.id, valor.id)}
												>
													{/* El servidor usa "nombre" para el valor */}
													{valor.nombre || valor.valor}
												</Button>
											))}
										</div>
									) : (
										/* Mostrar como select si hay muchos valores */
										<Form.Select
											value={selecciones[tipo.id] || ''}
											onChange={(e) => handleSeleccionChange(tipo.id, e.target.value)}
										>
											<option value="">Seleccionar {tipo.nombre}...</option>
											{tipo.valores.map(valor => (
												<option key={valor.id} value={valor.id}>
													{/* El servidor usa "nombre" para el valor */}
													{valor.nombre || valor.valor}
												</option>
											))}
										</Form.Select>
									)}
								</Form.Group>
							))}
						</div>

						{/* Resumen de selección */}
						{seleccionCompleta() && getResumenSeleccion().length > 0 && (
							<div className="seleccion-resumen mt-3 p-3 bg-light rounded">
								<small className="text-muted d-block mb-2">Selección:</small>
								<div className="d-flex flex-wrap gap-2">
									{getResumenSeleccion().map((sel, idx) => (
										<Badge
											key={idx}
											bg="primary"
											className="seleccion-badge"
										>
											{sel.tipoNombre}: {sel.valorNombre}
										</Badge>
									))}
								</div>
							</div>
						)}
					</>
				)}

				{/* Cantidad */}
				<Form.Group className="mt-4">
					<Form.Label className="fw-bold">Cantidad</Form.Label>
					<div className="cantidad-control d-flex align-items-center">
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={() => setCantidad(Math.max(1, cantidad - 1))}
							disabled={cantidad <= 1}
						>
							<i className="fa fa-minus"></i>
						</Button>
						<Form.Control
							type="number"
							min="1"
							value={cantidad}
							onChange={(e) => setCantidad(Math.max(1, parseInt(e.target.value) || 1))}
							className="mx-2 text-center cantidad-input"
							style={{ width: '80px' }}
						/>
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={() => setCantidad(cantidad + 1)}
						>
							<i className="fa fa-plus"></i>
						</Button>
					</div>
				</Form.Group>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="outline-secondary" onClick={handleCancel}>
					<i className="fa fa-times me-2"></i>
					Cancelar
				</Button>
				<Button
					variant="success"
					onClick={handleConfirm}
					disabled={loading || (atributosDisponibles.length > 0 && !seleccionCompleta())}
				>
					<i className="fa fa-plus me-2"></i>
					Agregar al Pedido
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default VarianteSelector